import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { projectService, userService, rotationService } from '../services/api';
import html2canvas from 'html2canvas';
import './ProjectDetail.css';
import ProjectRequiredDocs from './ProjectRequiredDocs';
import ProjectWorkTasks from './ProjectWorkTasks';

const ProjectDetail = ({ user: userProp }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const getWeekStartDate = (weekOffset = 0) => {
    const date = new Date();
    const day = date.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    date.setDate(date.getDate() + mondayOffset + (weekOffset * 7));
    return date.toISOString().split('T')[0];
  };


  const [project, setProject] = useState(null);
  const [users, setUsers] = useState([]);
  const [rotations, setRotations] = useState({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRotationSettings, setShowRotationSettings] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProject, setEditedProject] = useState(null);
  const [currentViewMonth, setCurrentViewMonth] = useState(new Date().getMonth());
  const [currentViewYear, setCurrentViewYear] = useState(new Date().getFullYear());
  const [copyWeekData, setCopyWeekData] = useState({
    source_start_date: getWeekStartDate(0),
    target_start_date: getWeekStartDate(1),
    user_id: '',
    overwrite: false
  });
  const [isCopyingWeek, setIsCopyingWeek] = useState(false);

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [managerEmail, setManagerEmail] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const canEditRequiredDocs = userProp && (userProp.role === 'admin' || userProp.role === 'supervisor');
  const [showEmailSubscriptions, setShowEmailSubscriptions] = useState(false);
  const canEditTasks = userProp && (userProp.role === 'admin' || userProp.role === 'manager' || userProp.role === 'supervisor');

  const [emailSubscriptions, setEmailSubscriptions] = useState([]);
  const [newSubscriptionEmail, setNewSubscriptionEmail] = useState('');
  const [newSubscriptionName, setNewSubscriptionName] = useState('');
  const [newSubscriptionDay, setNewSubscriptionDay] = useState(1);
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(false);
  const monthlyOverviewRef = useRef(null);

  // Funkcia pre zmenu stavu dňa (len pre admin)
  const handleDayClick = async (dateStr, userId) => {
    console.log('🖱️ Klik na deň:', { dateStr, userId, userRole: userProp?.role });
    
    if (userProp?.role !== 'admin') {
      console.log('❌ User is not admin, returning');
      return;
    }
    
    const currentStatus = getRotationStatus(userId, dateStr);
    console.log('📅 Current status:', currentStatus);
    
    let newStatus;
    
    // Cyklické prepínanie: unset -> work -> home -> sick -> unset
    switch (currentStatus) {
      case 'work':
        newStatus = 'home';
        break;
      case 'home':
        newStatus = 'sick';
        break;
      case 'sick':
        newStatus = null; // unset
        break;
      default:
        newStatus = 'work';
    }
    
    console.log('🔄 Changing status from', currentStatus, 'to', newStatus);
    
    try {
      console.log('📡 Calling API:', { userId, projectId, date: dateStr, status: newStatus });
      await rotationService.saveUserRotation(userId, projectId, {
        date: dateStr,
        status: newStatus
      });
      console.log('✅ API call successful, reloading rotations');
      await loadRotations(users); // Reload rotations
    } catch (error) {
      console.error('❌ Error updating day status:', error);
      alert('Chyba pri aktualizácii stavu dňa');
    }
  };

  useEffect(() => {
    if (projectId) {
      loadProjectDetails();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadProjectDetails = async () => {
    try {
      console.log('Loading project details for project:', projectId);
      setLoading(true);
      setError(null);
      
      // Load project details
      const projectData = await projectService.getById(projectId);
      setProject(projectData);
      
      // Load users assigned to this project (exclude blocked users)
      const allUsers = await userService.getAll();
      const projectUsers = allUsers.filter(user => 
        user.project_id === parseInt(projectId) && !user.blocked
      );
      setUsers(projectUsers);
      
      // Load existing rotations
      await loadRotations(projectUsers);
      
    } catch (error) {
      console.error('Error loading project details:', error);
      setError('Chyba pri načítavaní detailov projektu');
    } finally {
      setLoading(false);
    }
  };

  const loadRotations = async (projectUsers) => {
    try {
      console.log('Loading rotations for project:', projectId, 'with users:', projectUsers);
      
      // Set date range for entire year (from January 1st to December 31st)
      const currentYear = new Date().getFullYear();
      const startDate = `${currentYear}-01-01`;
      const endDate = project?.rotation_end_date || `${currentYear}-12-31`;
      
      console.log('Loading rotations from', startDate, 'to', endDate);
      
      // Load rotations for the project with date limit
      const data = await rotationService.getProjectRotations(projectId, startDate, endDate);
      const rotationData = {};
        
      // Group rotations by user ID
      data.rotations.forEach(rotation => {
        if (!rotationData[rotation.user_id]) {
          rotationData[rotation.user_id] = [];
        }
        rotationData[rotation.user_id].push({
          date: rotation.date,
          shift: rotation.shift
        });
      });
      
      console.log('Loaded rotations:', rotationData);
      setRotations(rotationData);
    } catch (error) {
      console.error('Error loading rotations:', error);
      setRotations({});
    }
  };

  const generateRotationCalendar = (userId, startDate) => {
    if (!startDate) return [];
    
    const calendar = [];
    const start = new Date(startDate);
    
    // Ensure we start on Monday
    const dayOfWeek = start.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    start.setDate(start.getDate() + mondayOffset);
    
    // Generate 6 weeks (4 work + 2 home) cycle for next 6 months
    for (let cycle = 0; cycle < 26; cycle++) { // 26 cycles = ~6 months
      // 4 weeks work (28 days)
      for (let day = 0; day < 28; day++) {
        const currentDate = new Date(start);
        currentDate.setDate(start.getDate() + (cycle * 42) + day);
        calendar.push({
          date: currentDate.toISOString().split('T')[0],
          status: 'work',
          week: Math.floor(day / 7) + 1,
          dayOfWeek: currentDate.getDay()
        });
      }
      
      // 2 weeks home (14 days)
      for (let day = 28; day < 42; day++) {
        const currentDate = new Date(start);
        currentDate.setDate(start.getDate() + (cycle * 42) + day);
        calendar.push({

          date: currentDate.toISOString().split('T')[0],
          status: 'home',
          week: Math.floor((day - 28) / 7) + 1,
          dayOfWeek: currentDate.getDay()
        });
      }
    }
    
    return calendar;
  };

  const saveRotation = async (userId, startDate) => {
    try {
      const calendar = generateRotationCalendar(userId, startDate);
      
      await rotationService.saveUserRotation(userId, projectId, {
        startDate,
        calendar
      });

      alert('Rotácia bola úspešne nastavená!');
      loadRotations(users);
    } catch (error) {
      console.error('Error saving rotation:', error);
      alert('Nastala chyba pri ukladaní rotácie');
    }
  };

  const handleCopyWeekChange = (field, value) => {
    setCopyWeekData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCopyWeekSubmit = async (e) => {
    e.preventDefault();

    if (copyWeekData.source_start_date === copyWeekData.target_start_date) {
      alert('Zdrojový a cieľový týždeň nemôžu byť rovnaké.');
      return;
    }

    if (copyWeekData.overwrite && !window.confirm('Prepísanie vymaže existujúce rotácie v cieľových dňoch pre vybraných ľudí. Pokračovať?')) {
      return;
    }

    try {
      setIsCopyingWeek(true);
      const payload = {
        source_start_date: copyWeekData.source_start_date,
        target_start_date: copyWeekData.target_start_date,
        project_id: parseInt(projectId, 10),
        overwrite: copyWeekData.overwrite
      };

      if (copyWeekData.user_id) {
        payload.user_id = parseInt(copyWeekData.user_id, 10);
      }

      const result = await rotationService.copyWeek(payload);
      await loadRotations(users);
      alert(`Týždeň bol skopírovaný. Vytvorené: ${result.copied}, preskočené: ${result.skipped}.`);
    } catch (error) {
      console.error('Error copying project week rotations:', error);
      alert('Nepodarilo sa skopírovať týždeň rotácií.');
    } finally {
      setIsCopyingWeek(false);
    }
  };

  const getRotationStatus = (userId, date) => {
    const userRotations = rotations[userId] || [];
    const rotation = userRotations.find(r => r.date === date);
    console.log(`Getting rotation for user ${userId} on ${date}:`, rotation ? rotation.shift : 'none');
    return rotation ? rotation.shift : null;
  };

  const handleEditProject = () => {
    setEditedProject({
      ...project,
      weekday_rate: project.weekday_rate || 0,
      weekend_rate: project.weekend_rate || 0,
      holiday_rate: project.holiday_rate || 0,
      accommodation_price_monthly: project.accommodation_price_monthly || 0
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedProject(null);
  };

  const handleSaveProject = async () => {
    try {
      const updatedProject = await projectService.update(projectId, editedProject);
      setProject(updatedProject);
      setIsEditing(false);
      setEditedProject(null);
      alert('Projekt bol úspešne aktualizovaný!');
    } catch (error) {
      console.error('Error updating project:', error);
      alert('Chyba pri aktualizácii projektu: ' + error.message);
    }
  };

  const handleInputChange = (field, value) => {
    setEditedProject(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Funkcia na otvorenie modalu pre email
  const handleOpenEmailModal = async () => {
    setShowEmailModal(true);
    setManagerEmail('');
    
    // Automaticky vygeneruj preview screenshotu
    try {
      const element = monthlyOverviewRef.current;
      if (element) {
        console.log('📸 Generujem preview screenshotu...');
        const canvas = await html2canvas(element, {
          scale: 2,
          backgroundColor: '#ffffff',
          logging: false
        });
        const previewUrl = canvas.toDataURL('image/png');
        setScreenshotPreview(previewUrl);
      }
    } catch (error) {
      console.error('❌ Chyba pri generovaní preview:', error);
    }
  };

  // Funkcia na vytvorenie screenshotu a odoslanie emailu
  const handleSendScreenshotEmail = async () => {
    if (!managerEmail || !managerEmail.includes('@')) {
      alert('Prosím zadajte platný email manažéra');
      return;
    }

    setIsSendingEmail(true);

    try {
      // Vytvor screenshot z mesačného prehľadu
      const element = monthlyOverviewRef.current;
      if (!element) {
        throw new Error('Element nenájdený');
      }

      console.log('📸 Vytvárám screenshot...');
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true
      });

      // Konvertuj canvas na blob
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      
      // Vytvor FormData pre odoslanie
      const formData = new FormData();
      formData.append('screenshot', blob, 'rotation-schedule.png');
      formData.append('email', managerEmail);
      formData.append('projectName', project.name);
      formData.append('month', new Date(currentViewYear, currentViewMonth).toLocaleDateString('sk-SK', { month: 'long', year: 'numeric' }));

      console.log('📧 Odosielam email...');
      await api.post('/send-rotation-email', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      alert('✅ Email bol úspešne odoslaný!');
      setShowEmailModal(false);
      setManagerEmail('');
    } catch (error) {
      console.error('❌ Chyba pri odosielaní emailu:', error);
      alert('Chyba pri odosielaní emailu: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsSendingEmail(false);
    }
  };

  // ==============================================
  // EMAIL SUBSCRIPTIONS FUNCTIONS
  // ==============================================

  // Načítať email subscriptions
  const loadEmailSubscriptions = async () => {
    if (!projectId) return;
    
    setIsLoadingSubscriptions(true);
    try {
      const response = await api.get(`/email-subscriptions/${projectId}`);
      setEmailSubscriptions(response.data);
      console.log('✅ Email subscriptions načítané:', response.data);
    } catch (error) {
      console.error('❌ Chyba pri načítaní subscriptions:', error);
      alert('Chyba pri načítaní email subscriptions');
    } finally {
      setIsLoadingSubscriptions(false);
    }
  };

  // Pridať novú subscription
  const handleAddSubscription = async () => {
    if (!newSubscriptionEmail || !newSubscriptionEmail.includes('@')) {
      alert('Prosím zadajte platný email');
      return;
    }

    try {
      await api.post('/email-subscriptions', {
        project_id: projectId,
        email: newSubscriptionEmail,
        recipient_name: newSubscriptionName,
        send_on_day: newSubscriptionDay
      });

      alert('✅ Email subscription úspešne pridaná!');
      setNewSubscriptionEmail('');
      setNewSubscriptionName('');
      setNewSubscriptionDay(1);
      loadEmailSubscriptions(); // Reload zoznamu
    } catch (error) {
      console.error('❌ Chyba pri pridávaní subscription:', error);
      alert(error.response?.data?.error || 'Chyba pri pridávaní email subscription');
    }
  };

  // Zmazať subscription
  const handleDeleteSubscription = async (subscriptionId) => {
    if (!window.confirm('Naozaj chcete zmazať túto email subscription?')) {
      return;
    }

    try {
      await api.delete(`/email-subscriptions/${subscriptionId}`);

      alert('✅ Email subscription úspešne zmazaná!');
      loadEmailSubscriptions(); // Reload zoznamu
    } catch (error) {
      console.error('❌ Chyba pri mazaní subscription:', error);
      alert('Chyba pri mazaní email subscription');
    }
  };

  // Toggle aktívny/neaktívny stav
  const handleToggleSubscription = async (subscriptionId) => {
    try {
      await api.patch(`/email-subscriptions/${subscriptionId}/toggle`, {});

      loadEmailSubscriptions(); // Reload zoznamu
    } catch (error) {
      console.error('❌ Chyba pri zmene stavu subscription:', error);
      alert('Chyba pri zmene stavu subscription');
    }
  };

  // Načítať subscriptions pri otvorení modalu
  const handleOpenSubscriptionsModal = () => {
    setShowEmailSubscriptions(true);
    loadEmailSubscriptions();
  };



  const renderMonthlyOverview = () => {
    const today = new Date();
    
    // Get month name in Slovak
    const monthNames = [
      'Január', 'Február', 'Marec', 'Apríl', 'Máj', 'Jún',
      'Júl', 'August', 'September', 'Október', 'November', 'December'
    ];
    
    const goToPreviousMonth = () => {
      if (currentViewMonth === 0) {
        setCurrentViewMonth(11);
        setCurrentViewYear(currentViewYear - 1);
      } else {
        setCurrentViewMonth(currentViewMonth - 1);
      }
    };

    const goToNextMonth = () => {
      if (currentViewMonth === 11) {
        setCurrentViewMonth(0);
        setCurrentViewYear(currentViewYear + 1);
      } else {
        setCurrentViewMonth(currentViewMonth + 1);
      }
    };
    
    const monthDays = new Date(currentViewYear, currentViewMonth + 1, 0).getDate();
    
    return (
      <div className="pdetail-monthly-overview" ref={monthlyOverviewRef}>
        <div className="pdetail-monthly-header">
          <button className="pdetail-month-nav-btn" onClick={goToPreviousMonth}>
            ◀ {monthNames[currentViewMonth === 0 ? 11 : currentViewMonth - 1]}
          </button>
          <h4>📅 Mesačný prehľad rotácie - {monthNames[currentViewMonth]} {currentViewYear}</h4>
          <button className="pdetail-month-nav-btn" onClick={goToNextMonth}>
            {monthNames[currentViewMonth === 11 ? 0 : currentViewMonth + 1]} ▶
          </button>
          {userProp?.role === 'admin' && (
            <>
              <button className="pdetail-email-btn" onClick={handleOpenEmailModal} title="Poslať email manažérovi">
                📧 Poslať emailom
              </button>
              <button className="pdetail-subscriptions-btn" onClick={handleOpenSubscriptionsModal} title="Spravovať automatické mesačné reporty">
                ⚙️ Auto-reporty
              </button>
            </>
          )}
        </div>
        <div className="pdetail-overview-table">
          <div className="pdetail-overview-header">
            <div className="pdetail-user-column">Meno používateľa</div>
            {Array.from({ length: monthDays }, (_, i) => (
              <div key={i + 1} className="pdetail-day-column">{i + 1}</div>
            ))}
          </div>
          {users.map(user => (
            <div key={user.id} className="pdetail-overview-row">
              <div className="pdetail-user-name">{user.full_name || user.username}</div>
              {Array.from({ length: monthDays }, (_, i) => {
                const day = i + 1;
                const dateStr = `${currentViewYear}-${String(currentViewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const status = getRotationStatus(user.id, dateStr);
                const isToday = day === today.getDate() && currentViewMonth === today.getMonth() && currentViewYear === today.getFullYear();

                const isAdmin = userProp?.role === 'admin';
                
                return (
                  <div
                    key={day}
                    className={`pdetail-overview-day ${status ? `pdetail-${status}` : 'pdetail-unset'} ${isToday ? 'pdetail-today' : ''} ${isAdmin ? 'pdetail-clickable' : ''}`}
                    title={`${day}. ${monthNames[currentViewMonth]} - ${user.full_name || user.username} - ${
                      status === 'work' ? 'Práca' :
                      status === 'home' ? 'Doma' :
                      status === 'sick' ? 'Choroba' : 'Nenastavené'
                    }${isAdmin ? ' (kliknite pre zmenu)' : ''}`}
                    onClick={isAdmin ? () => handleDayClick(dateStr, user.id) : undefined}
                    style={{ cursor: isAdmin ? 'pointer' : 'default' }}
                  >
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="pdetail-overview-legend">
          <div className="pdetail-legend-item">
            <div className="pdetail-legend-color pdetail-work"></div>
            <span>Work</span>
          </div>

          <div className="pdetail-legend-item">
            <div className="pdetail-legend-color pdetail-home"></div>
            <span>Home</span>
          </div>
          <div className="pdetail-legend-item">
            <div className="pdetail-legend-color pdetail-sick"></div>
            <span>Sick</span>
          </div>
          <div className="pdetail-legend-item">
            <div className="pdetail-legend-color pdetail-unset"></div>
            <span>Not Set</span>
          </div>
        </div>
      </div>
    );
  };

  const renderUserCalendar = (calendarUser) => {
    const today = new Date();
    const todayDay = today.getDate();
    const todayMonth = today.getMonth();
    const todayYear = today.getFullYear();
    
    // Use current view month instead of current week
    const firstDayOfMonth = new Date(currentViewYear, currentViewMonth, 1);
    const lastDayOfMonth = new Date(currentViewYear, currentViewMonth + 1, 0);
    
    // Find the Monday of the week containing the first day of the month
    const startOfCalendar = new Date(firstDayOfMonth);
    const dayOfWeek = startOfCalendar.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    startOfCalendar.setDate(startOfCalendar.getDate() + mondayOffset);
    
    // Find the Sunday of the week containing the last day of the month
    const endOfCalendar = new Date(lastDayOfMonth);
    const endDayOfWeek = endOfCalendar.getDay();
    const sundayOffset = endDayOfWeek === 0 ? 0 : 7 - endDayOfWeek;
    endOfCalendar.setDate(endOfCalendar.getDate() + sundayOffset);
    
    const weeks = [];
    const currentDate = new Date(startOfCalendar);
    
    while (currentDate <= endOfCalendar) {
      const weekDays = [];
      for (let day = 0; day < 7; day++) {
        // Use local date formatting to avoid timezone issues
        const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
        const status = getRotationStatus(calendarUser.id, dateStr);
        const isCurrentMonth = currentDate.getMonth() === currentViewMonth;
        const isToday = currentDate.getDate() === todayDay && 
                        currentDate.getMonth() === todayMonth && 
                        currentDate.getFullYear() === todayYear;
        
        weekDays.push({
          date: new Date(currentDate),
          dateStr,
          status,
          isToday,
          isCurrentMonth
        });
        
        currentDate.setDate(currentDate.getDate() + 1);
      }
      weeks.push(weekDays);
    }

    return (
      <div className="pdetail-user-calendar">
        <div className="pdetail-calendar-header">
          <div className="pdetail-day-header">Po</div>
          <div className="pdetail-day-header">Ut</div>
          <div className="pdetail-day-header">St</div>
          <div className="pdetail-day-header">Št</div>
          <div className="pdetail-day-header">Pi</div>
          <div className="pdetail-day-header">So</div>
          <div className="pdetail-day-header">Ne</div>
        </div>
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="pdetail-calendar-week">
            {week.map((day, dayIndex) => (
              <div 
                key={dayIndex} 
                className={`pdetail-calendar-day ${day ? (day.status ? `pdetail-${day.status}` : 'pdetail-unset') : 'pdetail-empty'} ${day?.isToday ? 'pdetail-today' : ''} ${day && !day.isCurrentMonth ? 'pdetail-other-month' : ''} ${userProp?.role === 'admin' && day ? 'pdetail-clickable' : ''}`}
                title={day ? `${day.date.toLocaleDateString('sk-SK')} - ${
                  day.status === 'work' ? 'Práca' : 
                  day.status === 'home' ? 'Doma' :
                  day.status === 'sick' ? 'Choroba' : 'Nenastavené'
                }${userProp?.role === 'admin' ? ' (kliknite pre zmenu)' : ''}` : ''}
                onClick={day && userProp?.role === 'admin' ? () => handleDayClick(day.dateStr, calendarUser.id) : undefined}
                style={{ cursor: day && userProp?.role === 'admin' ? 'pointer' : 'default' }}
              >
                {day ? day.date.getDate() : ''}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="pdetail-project-detail-container">
        <div className="pdetail-loading-pdetail-spinner">
          <div className="pdetail-spinner"></div>
          <p>Načítavam detail projektu...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pdetail-project-detail-container">
        <div className="pdetail-error-message">
          <h2>Chyba</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/projects')} className="pdetail-back-btn">
            ← Späť na projekty
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pdetail-project-detail-container">
      <div className="pdetail-project-detail-header">
        <button onClick={() => navigate('/projects')} className="pdetail-back-btn">
          ← Späť na projekty
        </button>
        <h1>📋 {project?.name || `Projekt #${projectId}`}</h1>
        {canEditTasks && (
          <button 
            onClick={() => setShowRotationSettings(!showRotationSettings)}
            className="pdetail-rotation-settings-btn"
          >
            ⚙️ Nastaviť rotáciu
          </button>
        )}
      </div>

      {project && (
        <div className="pdetail-project-info-section">
          <div className="pdetail-project-basic-info">
            <div className="pdetail-project-info-header">
              <h3>📝 Informácie o projekte</h3>
              {userProp?.role === 'admin' && !isEditing && (
                <button onClick={handleEditProject} className="pdetail-edit-project-btn">
                  ✏️ Upraviť
                </button>
              )}
            </div>

            {!isEditing ? (
              <div className="project-info-display">
                <p><strong>Popis:</strong> {project.description || 'Bez popisu'}</p>
                <p><strong>Minimálny počet ľudí v práci:</strong> {project.min_people_required || 3}</p>
                <p><strong>Cena za ubytovanie:</strong> {project.accommodation_price_monthly ? `${project.accommodation_price_monthly}€/mesiac` : 'Nezadané'}</p>
                {project.accommodation_price_monthly && (
                  <p><strong>Prepočítaná denná cena:</strong> {project.accommodation_price ? `${project.accommodation_price.toFixed(2)}€/deň/osoba` : 'Neprepočítané'}</p>
                )}
                <p><strong>Rotácia:</strong> {project.rotation_weeks_work || 4} týždňov práca / {project.rotation_weeks_home || 2} týždňov doma</p>
                <p><strong>Limit rotácií:</strong> {project.rotation_end_date ? `do ${new Date(project.rotation_end_date).toLocaleDateString('sk-SK')}` : 'do 31. 12. 2025'}</p>
                <p><strong>Obdobie:</strong> {project.start_date && project.end_date ? `${new Date(project.start_date).toLocaleDateString('sk-SK')} - ${new Date(project.end_date).toLocaleDateString('sk-SK')}` : 'Nezadané'}</p>
                <p><strong>Vytvorený:</strong> {project.created_at ? new Date(project.created_at).toLocaleDateString('sk-SK') : 'Neznámy dátum'}</p>
                
                <div className="pdetail-billing-rates-section">
                  <h4>💰 Fakturačné sadzby</h4>
                  <p><strong>Pracovný deň:</strong> {project.weekday_rate || 0}€/hod</p>
                  <p><strong>Víkend:</strong> {project.weekend_rate || 0}€/hod</p>
                  <p><strong>Sviatok:</strong> {project.holiday_rate || 0}€/hod</p>
                </div>

                {/* 📄 Požadované dokumenty presunuté sem (na koniec Info o projekte) */}
                <div style={{ marginTop:'16px' }}>
                  <ProjectRequiredDocs projectId={projectId} canEdit={canEditRequiredDocs} />
                  <ProjectWorkTasks projectId={projectId} canEdit={canEditTasks} />
                </div>
              </div>
            ) : (
              <div className="pdetail-project-edit-form">
                <div className="pdetail-form-group">
                  <label><strong>Názov projektu:</strong></label>
                  <input
                    type="text"
                    value={editedProject?.name || ''}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="pdetail-form-input"
                  />
                </div>

                <div className="pdetail-form-group">
                  <label><strong>Popis:</strong></label>
                  <textarea
                    value={editedProject?.description || ''}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="pdetail-form-textarea"
                    rows="3"
                  />
                </div>

                <div className="pdetail-form-row">
                  <div className="pdetail-form-group">
                    <label><strong>Minimálny počet ľudí:</strong></label>
                    <input
                      type="number"
                      min="1"
                      value={editedProject?.min_people_required || 3}
                      onChange={(e) => handleInputChange('min_people_required', parseInt(e.target.value))}
                      className="pdetail-form-input"
                    />
                  </div>

                  <div className="pdetail-form-group">
                    <label><strong>Cena za ubytovanie (€/mesiac):</strong></label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editedProject?.accommodation_price_monthly || ''}
                      onChange={(e) => handleInputChange('accommodation_price_monthly', parseFloat(e.target.value))}
                      className="pdetail-form-input"
                    />
                    <small className="pdetail-form-help">Denná cena sa prepočíta automaticky podľa počtu dní v mesiaci a aktívnych ľudí</small>
                  </div>
                </div>

                <div className="pdetail-form-row">
                  <div className="pdetail-form-group">
                    <label><strong>Týždne práca:</strong></label>
                    <input
                      type="number"
                      min="1"
                      value={editedProject?.rotation_weeks_work || 4}
                      onChange={(e) => handleInputChange('rotation_weeks_work', parseInt(e.target.value))}
                      className="pdetail-form-input"
                    />
                  </div>

                  <div className="pdetail-form-group">
                    <label><strong>Týždne doma:</strong></label>
                    <input
                      type="number"
                      min="1"
                      value={editedProject?.rotation_weeks_home || 2}
                      onChange={(e) => handleInputChange('rotation_weeks_home', parseInt(e.target.value))}
                      className="pdetail-form-input"
                    />
                  </div>
                </div>

                <div className="pdetail-billing-rates-edit">
                  <h4>💰 Fakturačné sadzby</h4>
                  <div className="pdetail-form-row">
                    <div className="pdetail-form-group">
                      <label><strong>Pracovný deň (€/hod):</strong></label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={editedProject?.weekday_rate || 0}
                        onChange={(e) => handleInputChange('weekday_rate', parseFloat(e.target.value) || 0)}
                        className="pdetail-form-input"
                      />
                    </div>

                    <div className="pdetail-form-group">
                      <label><strong>Víkend (€/hod):</strong></label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={editedProject?.weekend_rate || 0}
                        onChange={(e) => handleInputChange('weekend_rate', parseFloat(e.target.value) || 0)}
                        className="pdetail-form-input"
                      />
                    </div>

                    <div className="pdetail-form-group">
                      <label><strong>Sviatok (€/hod):</strong></label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={editedProject?.holiday_rate || 0}
                        onChange={(e) => handleInputChange('holiday_rate', parseFloat(e.target.value) || 0)}
                        className="pdetail-form-input"
                      />
                    </div>
                  </div>

                </div>

                <div className="pdetail-form-pdetail-actions">
                  <button onClick={handleSaveProject} className="pdetail-save-btn">
                    💾 Uložiť
                  </button>
                  <button onClick={handleCancelEdit} className="pdetail-cancel-btn">
                    ❌ Zrušiť
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="pdetail-users-rotation-section">
        <h3>👥 Používatelia a rotácia ({users.length})</h3>

        {canEditTasks && showRotationSettings && users.length > 0 && (
          <form className="pdetail-copy-week-panel" onSubmit={handleCopyWeekSubmit}>
            <div className="pdetail-copy-week-heading">
              <h4>📋 Kopírovať týždeň rotácií</h4>
              <p>Skopíruje rotácie tohto projektu zo zdrojového týždňa do cieľového týždňa.</p>
            </div>
            <div className="pdetail-copy-week-fields">
              <div className="pdetail-copy-week-field">
                <label>Zdrojový týždeň od</label>
                <input
                  type="date"
                  value={copyWeekData.source_start_date}
                  onChange={(e) => handleCopyWeekChange('source_start_date', e.target.value)}
                  required
                />
              </div>
              <div className="pdetail-copy-week-field">
                <label>Cieľový týždeň od</label>
                <input
                  type="date"
                  value={copyWeekData.target_start_date}
                  onChange={(e) => handleCopyWeekChange('target_start_date', e.target.value)}
                  required
                />
              </div>
              <div className="pdetail-copy-week-field">
                <label>Používateľ</label>
                <select
                  value={copyWeekData.user_id}
                  onChange={(e) => handleCopyWeekChange('user_id', e.target.value)}
                >
                  <option value="">Všetci na projekte</option>
                  {users.map(projectUser => (
                    <option key={projectUser.id} value={projectUser.id}>
                      {projectUser.full_name || projectUser.username || projectUser.email}
                    </option>
                  ))}
                </select>
              </div>
              <label className="pdetail-copy-week-checkbox">
                <input
                  type="checkbox"
                  checked={copyWeekData.overwrite}
                  onChange={(e) => handleCopyWeekChange('overwrite', e.target.checked)}
                />
                Prepísať existujúce cieľové dni
              </label>
              <button type="submit" className="pdetail-copy-week-btn" disabled={isCopyingWeek}>
                {isCopyingWeek ? 'Kopírujem...' : 'Kopírovať týždeň'}
              </button>
            </div>
          </form>
        )}

        
        {users.length > 0 && renderMonthlyOverview()}
        
        {users.length > 0 ? (
          <div className="pdetail-users-rotation-list">
            {users.map(projectUser => (
              <div key={projectUser.id} className="pdetail-user-rotation-card">
                <div className="pdetail-user-info">
                  <div className="pdetail-user-avatar">
                    {projectUser.role === 'admin' ? '👨‍💼' : '👤'}
                  </div>
                  <div className="pdetail-user-details">
                    <h4>{projectUser.full_name}</h4>
                    <p className="pdetail-user-role">
                      {projectUser.role === 'admin' ? 'Administrátor' : 
                       projectUser.role === 'supervisor' ? 'Supervízor' :
                       projectUser.role === 'manager' ? 'Manažér' : 'Zamestnanec'}
                    </p>
                    <p className="pdetail-user-email">{projectUser.email}</p>
                  </div>
                  
                  {userProp?.role === 'admin' && showRotationSettings && (
                    <div className="pdetail-rotation-controls">
                      <label>Prvý deň v práci:</label>
                      <input 
                        type="date" 
                        onChange={(e) => {
                          if (e.target.value) {
                            saveRotation(projectUser.id, e.target.value);
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
                
                <div className="pdetail-user-calendar-container">
                  <div className="pdetail-calendar-wrapper">
                    {renderUserCalendar(projectUser)}
                  </div>
                  <div className="pdetail-calendar-info">
                    <h5>📅 Kalendár rotácie</h5>
                    <div className="pdetail-calendar-legend">
                      <div className="pdetail-legend-item pdetail-work">
                        <div className="pdetail-legend-color pdetail-work"></div>
                        <span>Práca</span>
                      </div>
                      <div className="pdetail-legend-item pdetail-home">
                        <div className="pdetail-legend-color pdetail-home"></div>
                        <span>Doma</span>
                      </div>
                      <div className="pdetail-legend-item pdetail-unset">
                        <div className="pdetail-legend-color pdetail-unset"></div>
                        <span>Nenastavené</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="pdetail-no-users">
            <p>Žiadni používatelia nie sú priradení k tomuto projektu.</p>
          </div>
        )}
      </div>
      
      <div className="pdetail-system-info">
        <p><strong>ConstructAP s.r.o.</strong> - Dochádzový systém v 2.9.11</p>
        <p>Posledná aktualizácia: {new Date().toLocaleString('sk-SK')}</p>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="pdetail-modal-overlay" onClick={() => setShowEmailModal(false)}>
          <div className="pdetail-modal-content pdetail-email-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pdetail-modal-header">
              <h3>📧 Poslať rotáciu emailom</h3>
              <button className="pdetail-modal-close" onClick={() => setShowEmailModal(false)}>×</button>
            </div>
            <div className="pdetail-modal-body">
              <p>
                Odoslanie mesačného prehľadu rotácie pre projekt <strong>{project?.name}</strong>
              </p>
              
              {/* Preview screenshotu */}
              {screenshotPreview && (
                <div className="pdetail-screenshot-preview">
                  <h4>📸 Náhľad screenshotu:</h4>
                  <div className="pdetail-preview-container">
                    <img 
                      src={screenshotPreview} 
                      alt="Preview rotácie" 
                      className="pdetail-preview-image"
                    />
                  </div>
                  <p className="pdetail-preview-info">
                    ✅ Screenshot je pripravený na odoslanie
                  </p>
                </div>
              )}
              
              <div className="pdetail-form-group">
                <label htmlFor="managerEmail">Email manažéra:</label>
                <input
                  type="email"
                  id="managerEmail"
                  className="pdetail-form-control"
                  placeholder="manazer@example.com"
                  value={managerEmail}
                  onChange={(e) => setManagerEmail(e.target.value)}
                  disabled={isSendingEmail}
                />
              </div>
              <p className="pdetail-info-text">
                ℹ️ Screenshot tabuľky rotácie bude odoslaný ako príloha v emaile.
              </p>
            </div>
            <div className="pdetail-modal-footer">
              <button 
                className="pdetail-btn-secondary" 
                onClick={() => setShowEmailModal(false)}
                disabled={isSendingEmail}
              >
                Zrušiť
              </button>
              <button 
                className="pdetail-btn-primary" 
                onClick={handleSendScreenshotEmail}
                disabled={isSendingEmail || !managerEmail}
              >
                {isSendingEmail ? '📧 Odosielam...' : '📧 Odoslať'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Subscriptions Modal */}
      {showEmailSubscriptions && (
        <div className="pdetail-modal-overlay" onClick={() => setShowEmailSubscriptions(false)}>
          <div className="pdetail-modal-content pdetail-subscriptions-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pdetail-modal-header">
              <h3>⚙️ Automatické mesačné reporty</h3>
              <button className="pdetail-modal-close" onClick={() => setShowEmailSubscriptions(false)}>×</button>
            </div>
            
            <div className="pdetail-modal-body">
              <div className="pdetail-subscriptions-info">
                <p>
                  📅 Nastavte emailové adresy, na ktoré sa má automaticky posielať mesačný prehľad rotácie.
                  Systém odošle report každý mesiac v zvolený deň.
                </p>
              </div>

              {/* Formulár na pridanie novej subscription */}
              <div className="pdetail-add-subscription-form">
                <h4>➕ Pridať nový email</h4>
                <div className="pdetail-form-row">
                  <div className="pdetail-form-group">
                    <label htmlFor="subEmail">Email adresa *</label>
                    <input
                      type="email"
                      id="subEmail"
                      className="pdetail-form-control"
                      placeholder="manazer@example.com"
                      value={newSubscriptionEmail}
                      onChange={(e) => setNewSubscriptionEmail(e.target.value)}
                    />
                  </div>
                  <div className="pdetail-form-group">
                    <label htmlFor="subName">Meno príjemcu</label>
                    <input
                      type="text"
                      id="subName"
                      className="pdetail-form-control"
                      placeholder="Ing. Ján Novák"
                      value={newSubscriptionName}
                      onChange={(e) => setNewSubscriptionName(e.target.value)}
                    />
                  </div>
                  <div className="pdetail-form-group">
                    <label htmlFor="subDay">Deň v mesiaci (1-28)</label>
                    <input
                      type="number"
                      id="subDay"
                      className="pdetail-form-control"
                      min="1"
                      max="28"
                      value={newSubscriptionDay}
                      onChange={(e) => setNewSubscriptionDay(parseInt(e.target.value))}
                    />
                  </div>
                  <button 
                    className="pdetail-btn-add-subscription" 
                    onClick={handleAddSubscription}
                    disabled={!newSubscriptionEmail}
                  >
                    ➕ Pridať
                  </button>
                </div>
              </div>

              {/* Zoznam existujúcich subscriptions */}
              <div className="pdetail-subscriptions-list">
                <h4>📧 Aktívne email subscriptions</h4>
                {isLoadingSubscriptions ? (
                  <div className="loading">Načítavam...</div>
                ) : emailSubscriptions.length === 0 ? (
                  <div className="pdetail-no-subscriptions">
                    <p>Zatiaľ nie sú nastavené žiadne automatické reporty.</p>
                  </div>
                ) : (
                  <div className="pdetail-subscriptions-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Príjemca</th>
                          <th>Deň odoslania</th>
                          <th>Vytvorené</th>
                          <th>Stav</th>
                          <th>Akcie</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailSubscriptions.map(sub => (
                          <tr key={sub.id} className={sub.active ? '' : 'inactive'}>
                            <td>{sub.email}</td>
                            <td>{sub.recipient_name || '-'}</td>
                            <td>{sub.send_on_day}. deň v mesiaci</td>
                            <td>{new Date(sub.created_at).toLocaleDateString('sk-SK')}</td>
                            <td>
                              <span className={`status-badge ${sub.active ? 'active' : 'inactive'}`}>
                                {sub.active ? '✅ Aktívny' : '⏸️ Neaktívny'}
                              </span>
                            </td>
                            <td className="pdetail-actions">
                              <button 
                                className="pdetail-btn-toggle" 
                                onClick={() => handleToggleSubscription(sub.id)}
                                title={sub.active ? 'Deaktivovať' : 'Aktivovať'}
                              >
                                {sub.active ? '⏸️' : '▶️'}
                              </button>
                              <button 
                                className="pdetail-btn-delete" 
                                onClick={() => handleDeleteSubscription(sub.id)}
                                title="Zmazať"
                              >
                                🗑️
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="pdetail-subscriptions-footer-info">
                <p>
                  ℹ️ <strong>Info:</strong> Cron job beží každý deň o 8:00 ráno. Ak dnes je nastavený deň odoslania, 
                  report sa automaticky odošle na všetky aktívne email adresy.
                </p>
                <p>
                  📊 Report obsahuje mesačný prehľad rotácie pre projekt <strong>{project?.name}</strong> 
                  za predchádzajúci mesiac.
                </p>
              </div>
            </div>

            <div className="pdetail-modal-footer">
              <button 
                className="pdetail-btn-secondary" 
                onClick={() => setShowEmailSubscriptions(false)}
              >
                Zavrieť
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;
